import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  ArrowLeft,
  Hash,
  Calendar,
  User,
  Building2,
  BookOpenCheck,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";
import BrandName from "@/components/site/BrandName";
import Brand from "@/components/site/Brand";
import ScaledCertificate from "@/components/certificate/ScaledCertificate";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VerifyCertificate() {
  const { certId } = useParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [state, setState] = useState({
    // `/verify` with no reference is a valid entry point: it is the address
    // printed under "Scan to verify" on every certificate, so it has to
    // resolve to a lookup rather than falling through to the marketing site.
    status: certId ? "loading" : "lookup",
    data: null,
    error: "",
  });

  function handleLookup(e) {
    e.preventDefault();
    const ref = query.trim();
    if (!ref) return;
    navigate(`/verify/${encodeURIComponent(ref)}`);
  }

  useEffect(() => {
    if (!certId) {
      setState({ status: "lookup", data: null, error: "" });
      return undefined;
    }
    let cancelled = false;
    async function run() {
      try {
        const res = await fetch(`${API}/verify/${encodeURIComponent(certId)}`);
        if (res.status === 404) {
          if (!cancelled) setState({ status: "not-found", data: null, error: "" });
          return;
        }
        if (!res.ok) throw new Error("verification-failed");
        const data = await res.json();
        if (!cancelled) setState({ status: "found", data, error: "" });
      } catch (e) {
        if (!cancelled) setState({ status: "error", data: null, error: String(e.message || e) });
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [certId]);

  return (
    <div
      data-testid="verify-page"
      className="min-h-screen bg-[#040914] text-slate-100 overflow-x-hidden"
    >
      {/* Slim top bar */}
      <header className="border-b border-white/[0.06] bg-[#050B18]/85 backdrop-blur">
        <div className="mx-auto max-w-6xl px-5 lg:px-8 h-16 flex items-center justify-between">
          <Brand />
          <Link
            to="/"
            data-testid="verify-back-home"
            className="inline-flex items-center gap-1.5 text-[12px] uppercase tracking-[0.18em] text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={12} /> Back to site
          </Link>
        </div>
      </header>

      <main className="relative py-12 md:py-16">
        <div className="absolute inset-0 pointer-events-none grid-lines opacity-25" />

        <div className="relative mx-auto max-w-6xl px-5 lg:px-8">
          <div className="eyebrow">Certificate verification</div>
          <h1 className="mt-3 font-display text-3xl md:text-[44px] tracking-[-0.03em] text-white leading-[1.05] font-semibold">
            Public record.{" "}
            <span className="text-[#D4A65A]">Verified by <BrandName />.</span>
          </h1>

          {state.status === "loading" && (
            <div
              data-testid="verify-loading"
              className="mt-10 flex items-center gap-3 text-slate-400 text-[14px]"
            >
              <span className="h-2 w-2 rounded-full bg-[#D4A65A] animate-pulse" />
              Looking up certificate {certId}…
            </div>
          )}

          {state.status === "lookup" && (
            <div data-testid="verify-lookup" className="mt-8 max-w-xl">
              <p className="text-slate-400 text-[14px] leading-relaxed">
                Enter the certificate reference printed beneath{" "}
                <span className="text-slate-300">Scan to verify</span> on the
                certificate. It looks like{" "}
                <span className="font-mono text-slate-300">CC.2026.XXXXXXXX</span>.
              </p>

              <form
                onSubmit={handleLookup}
                className="mt-6 flex flex-col sm:flex-row gap-3"
              >
                <input
                  data-testid="verify-lookup-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="CC.2026.XXXXXXXX"
                  aria-label="Certificate reference"
                  autoComplete="off"
                  spellCheck={false}
                  className="flex-1 min-w-0 rounded-md border border-white/[0.10] bg-white/[0.03] px-4 py-3 text-[14px] font-mono text-white placeholder:text-slate-600 outline-none transition-colors focus:border-[#D4A65A]/60"
                />
                <button
                  data-testid="verify-lookup-submit"
                  type="submit"
                  className="shrink-0 rounded-md bg-[#D4A65A] px-5 py-3 text-[12px] uppercase tracking-[0.18em] font-semibold text-[#0B1424] transition-colors hover:bg-[#E0B56D]"
                >
                  Verify certificate
                </button>
              </form>

              <p className="mt-5 text-[12px] text-slate-500 leading-relaxed">
                Records are checked against the <BrandName /> immutable audit
                log and remain verifiable for seven years from issue.
              </p>
            </div>
          )}

          {state.status === "not-found" && (
            <div
              data-testid="verify-not-found"
              className="mt-10 rounded-2xl border border-rose-400/20 bg-rose-500/5 p-8 md:p-10 text-center"
            >
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-rose-400/30 bg-rose-500/10">
                <AlertTriangle className="text-rose-300" size={22} />
              </div>
              <h2 className="mt-5 font-display text-2xl md:text-3xl text-white tracking-[-0.02em] font-semibold">
                Certificate not found.
              </h2>
              <p className="mt-2 text-slate-400 text-[14px] max-w-md mx-auto">
                We could not find a record for{" "}
                <span className="font-mono text-slate-300">{certId}</span>.
                Please check the reference and try again, or request the
                certificate directly from the issuing organisation.
              </p>
              <Link
                to="/verify"
                data-testid="verify-try-again"
                className="mt-6 inline-flex items-center gap-1.5 text-[12px] uppercase tracking-[0.18em] text-[#D4A65A] transition-colors hover:text-white"
              >
                Try another reference
              </Link>
            </div>
          )}

          {state.status === "error" && (
            <div
              data-testid="verify-error"
              className="mt-10 rounded-md border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-[13px] text-rose-200"
            >
              Verification service is temporarily unavailable. Please try
              again in a moment.
            </div>
          )}

          {state.status === "found" && state.data && (
            <motion.div
              data-testid="verify-record"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
            >
              {/* ── 1. The full premium certificate (scaled to fit any device) ── */}
              <div className="mt-8 mx-auto max-w-5xl" data-testid="verify-certificate">
                <ScaledCertificate
                  staffName={state.data.name}
                  staffRole={state.data.staff_role || ""}
                  organisation={state.data.organisation || ""}
                  competencyTitle={state.data.module}
                  score={`${state.data.score_pct}%`}
                  passMark={state.data.pass_mark_pct ? `${state.data.pass_mark_pct}%` : "80%"}
                  completedOn={state.data.completed_on || new Date(state.data.issued_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                  validUntil={state.data.valid_until || "\u2014"}
                  certId={state.data.certificate_id}
                  version={state.data.version || "1.0"}
                />
              </div>

              {/* ── 2. Verification status ── */}
              <div className="mt-10 flex items-center justify-center">
                <span
                  data-testid="verify-status-pill"
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-[12px] uppercase tracking-[0.22em] font-semibold ${
                    state.data.passed
                      ? "border border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                      : "border border-amber-400/40 bg-amber-500/10 text-amber-300"
                  }`}
                >
                  <ShieldCheck size={14} />
                  {state.data.passed ? "Verified · Certificate is authentic" : "Verified · Assessment not passed"}
                </span>
              </div>

              {/* ── 3. Audit trail — for inspectors ── */}
              <div className="mt-10 rounded-2xl border border-white/[0.08] bg-gradient-to-br from-[#0B1424] to-[#050B18] p-6 md:p-10">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-slate-500 font-mono">
                  <span className="h-1 w-4 bg-[#D4A65A]/50" />
                  Verification detail — for inspectors
                </div>

                <dl className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-5">
                  <VField
                    icon={User}
                    label="Issued to"
                    value={state.data.name}
                    testid="verify-name"
                  />
                  <VField
                    icon={Building2}
                    label="Organisation"
                    value={state.data.organisation || "Independent practitioner"}
                    testid="verify-organisation"
                  />
                  <VField
                    icon={BookOpenCheck}
                    label="Module"
                    value={state.data.module}
                    testid="verify-module"
                  />
                  <VField
                    icon={CheckCircle2}
                    label="Score"
                    value={`${state.data.score} of ${state.data.total} · ${state.data.score_pct}%`}
                    testid="verify-score"
                  />
                  <VField
                    icon={Hash}
                    label="Certificate ID"
                    value={state.data.certificate_id}
                    testid="verify-cert-id"
                    mono
                  />
                  <VField
                    icon={Calendar}
                    label="Issued on"
                    value={new Date(state.data.issued_at).toLocaleString("en-GB", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                    testid="verify-issued"
                  />
                </dl>

                <div className="mt-8 pt-6 border-t border-white/[0.06] flex flex-wrap items-center gap-3 text-[10.5px] font-mono text-slate-500 uppercase tracking-[0.18em]">
                  <span className="inline-flex items-center gap-1.5 text-emerald-300">
                    <Hash size={11} /> SHA-256 chained
                  </span>
                  <span className="text-slate-600">·</span>
                  <span>Signed by CanniComply</span>
                  <span className="text-slate-600">·</span>
                  <span>Retained 7 years</span>
                </div>
              </div>

              <p className="mt-6 text-[12px] text-slate-500 leading-relaxed max-w-2xl">
                Verification is powered by the <BrandName /> immutable audit
                log. If you believe a certificate has been tampered with,
                please contact{" "}
                <a
                  href="mailto:wayne@ukcsl.co.uk"
                  className="text-[#D4A65A] hover:text-white"
                >
                  wayne@ukcsl.co.uk
                </a>
                .
              </p>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
}

function VField({ icon: Icon, label, value, testid, mono }) {
  return (
    <div>
      <dt className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-mono">
        <Icon size={11} className="text-[#D4A65A]" />
        {label}
      </dt>
      <dd
        data-testid={testid}
        className={`mt-1.5 text-white text-[15px] font-semibold tracking-tight leading-snug ${
          mono ? "font-mono" : ""
        }`}
      >
        {value}
      </dd>
    </div>
  );
}
